<script setup>
import { cloneDeep, pick } from 'lodash-es';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { getProcessedGeoJSON, useBiMapHelper } from '~/bi/components/widgets/map-widget/bi-map-helper.js';
import { useBiStore } from '~/bi/store/bi.store';
import { useMap } from '~/common/composables/mapbox/maps.js';

const props = defineProps({
  id: {
    type: String,
    default: () => crypto.randomUUID(),
  },
  data: {
    type: Array,
    default: null,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
  isPreview: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['ignoringNextConfigChange']);

const { CLUSTER_LAYER_IDS, HEATMAP_LAYER_ID, getSourcesLayers, addHeatmapLayer, addClusteringLayers, loadIcons, handleMouseEvents, handleClickEvents } = useBiMapHelper();
const bi_store = useBiStore();

let resize_observer = null;

const map_instance = ref(null);
const loading = ref(false);

const config = computed(() => ({
  // Column Alias Mapping
  column_alias_mapping: bi_store.alias_to_column_mapping(props.config?.query?.stages),

  // Layout Properties
  coordinates: props.config?.chart?.feature_coordinates || '',
  feature_value: props.config?.chart?.feature_value || '',
  map_style: props.config?.chart?.map_style || 'mapbox://styles/mapbox/streets-v11',
  enable_3d_globe: props.config?.chart?.enable_3d_globe || false,

  // Display Properties
  map_type: props.config?.chart?.map_type || 'default',
  feature_style: props.config?.chart?.feature_style || 'color',
  feature_color: props.config?.chart?.feature_color || '#ff3b3b',
  feature_point_size: props.config?.chart?.feature_point_size || 6,
  feature_icon: props.config?.chart?.feature_icon || 'asset',
  feature_icon_size: props.config?.chart?.feature_icon_size || 1,
  properties: props.config?.chart?.feature_properties || [],
  feature_popup_label: props.config?.chart?.feature_popup_label || '',

  // Cluster Properties
  cluster_color: props.config?.chart?.feature_cluster_color || '#51bbd6',
  show_labels: props.config?.chart?.show_labels !== 'false',
  prefix: props.config?.chart?.prefix || '',
  suffix: props.config?.chart?.suffix || '',

  // Heatmap Properties
  heatmap_options: props.config?.chart?.heatmap_options,

  // Map Config
  map_config: props.config?.chart?.map_config || {},
  conditional_formatting: props.config?.chart?.conditional_formatting || [],
}));

const { initMapbox, loadMapBoxPackage, addMapboxToken, removeMapboxInstance, setLayers } = useMap({}, async (event_data, event_name) => {
  if (event_name === 'loaded') {
    loadData();
    loading.value = false;
  }
});

function loadData(forceReconstruct = false) {
  if (!map_instance.value || !map_instance.value.loaded())
    return;

  const geojson = getProcessedGeoJSON(props.data, config);
  const base_config = {
    type: 'geojson',
    data: geojson,
  };

  if (config.value.map_type === 'cluster') {
    base_config.cluster = true;
    base_config.clusterMaxZoom = 14;
    base_config.clusterRadius = 50;
    base_config.clusterProperties = {
      display_value_sum: ['+', ['get', 'display_value']],
    };
  }

  const source_exists = map_instance.value.getSource('all_features_source');
  const { sources, layers } = getSourcesLayers();

  if (forceReconstruct || !source_exists) {
    if (source_exists) {
      // Remove existing layers and source
      const all_layer_ids = [HEATMAP_LAYER_ID, ...CLUSTER_LAYER_IDS, ...layers.map(layer => layer.id)];
      all_layer_ids.forEach((id) => {
        if (map_instance.value.getLayer(id)) {
          map_instance.value.removeLayer(id);
        }
      });
      sources.forEach((source) => {
        if (map_instance.value.getSource(source)) {
          map_instance.value.removeSource(source);
        }
      });
    }

    if (!map_instance.value.getSource('all_features_source')) {
      map_instance.value.addSource('all_features_source', base_config);
    }
    if (!map_instance.value.getSource('symbol-source')) {
      map_instance.value.addSource('symbol-source', {
        type: 'geojson',
        data: geojson,
      });
    }

    if (config.value.map_type === 'default') {
      const filter_layers = layers.filter((layer) => {
        if (layer.id === 'symbol-layer-icon') {
          return config.value.feature_style === 'icon';
        }
        return config.value.feature_style === 'color';
      });
      setLayers(filter_layers, map_instance.value);
      filter_layers.forEach((layer) => {
        handleClickEvents(layer.id, map_instance.value, config.value);
        handleMouseEvents(layer.id, map_instance.value);
      });
    }
    else if (config.value.map_type === 'cluster') {
      addClusteringLayers(map_instance.value, config.value);
    }
    else if (config.value.map_type === 'heatmap') {
      addHeatmapLayer(map_instance.value, config.value?.heatmap_options);
    }
  }
  else {
    map_instance.value.getSource('all_features_source').setData(geojson);
  }
}

async function initMap() {
  try {
    loading.value = true;
    await loadMapBoxPackage();
    await addMapboxToken();

    map_instance.value = await initMapbox({
      container_id: props.id,
      style: config.value?.map_style,
      center: config.value?.map_config?.center || [0, 20],
      zoom: config.value?.map_config?.zoom || 1,
      preserveDrawingBuffer: true,
    });

    map_instance.value.on('load', () => {
      loadIcons(map_instance.value);
      if (config.value.enable_3d_globe) {
        map_instance.value.setProjection('globe');
      }
      else {
        map_instance.value.setProjection('mercator');
        map_instance.value.setRenderWorldCopies(true);
      }
    });

    map_instance.value.on('moveend', () => {
      const map_config = {
        center: map_instance.value.getCenter().toArray(),
        zoom: map_instance.value.getZoom(),
      };
      if (props.isPreview) {
        bi_store.widget_builder_config.chart.map_config = map_config;
      }
      else {
        emit('ignoringNextConfigChange');
        const widget = bi_store.current_grid_layout.find(w => w.uid === props.id);
        if (widget) {
          const updated_config = cloneDeep(widget.config);
          updated_config.chart.map_config = map_config;
          bi_store.updateWidget(props.id, { config: updated_config });
        }
      }
    });

    resize_observer = new ResizeObserver(() => {
      map_instance.value.resize();
    });
    resize_observer.observe(document.getElementById(props.id));
  }
  catch (err) {
    logger.log('Map initialization failed:', err);
    loading.value = false;
  }
}

watch(() => pick(config.value, [
  'map_style',
  'map_type',
  'enable_3d_globe',
  'feature_value',
  'feature_style',
  'feature_color',
  'feature_icon',
  'feature_point_size',
  'feature_icon_size',
  'cluster_color',
  'show_labels',
  'prefix',
  'suffix',
  'feature_popup_label',
  'properties',
  'conditional_formatting',
  'heatmap_options',
]), (new_val, old_val) => {
  if (new_val?.zoom !== old_val?.zoom || new_val?.center !== old_val?.center) {
    return;
  }
  if (new_val?.enable_3d_globe !== old_val?.enable_3d_globe) {
    if (new_val.enable_3d_globe) {
      map_instance.value.setProjection('globe');
    }
    else {
      map_instance.value.setProjection('mercator');
    }
  }
  else if (new_val?.map_style !== old_val?.map_style) {
    map_instance.value.setStyle(config.value?.map_style);
    setTimeout(() => {
      loadData(true);
    }, 1000);
  }
  else {
    loadData(true);
  }
});

watch(() => props.data, () => {
  loadData(false);
}, { deep: true });

onMounted(() => {
  nextTick(() => {
    initMap();
  });
});

onUnmounted(() => {
  if (map_instance.value) {
    removeMapboxInstance(map_instance.value);
    resize_observer?.disconnect();
  }
});
</script>

<template>
  <div class="w-full h-full relative rounded-xl overflow-hidden">
    <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-gray-50">
      <div class="text-sm">
        Loading map...
      </div>
    </div>
    <div
      :id="id"
      class="w-full h-full"
    />
  </div>
</template>

<style lang="scss" scoped>
:deep(.mapboxgl-popup-close-button) {

  padding: 4px 8px;
  font-size: x-large;
}
:deep(.mapboxgl-popup-content) {
  border-radius: 8px;
  padding: 6px 8px;
  min-width: 250px;
  max-width: 300px;
}
:deep(.mapboxgl-ctrl-bottom-left), :deep(.mapboxgl-ctrl-bottom-right) {
  display: none !important;
}
</style>
